import { CloseIcon } from '@panwds/icons';
import { classNames, twMerge } from '@panwds/tailwindcss';
import { animated, useTransition } from '@react-spring/web';
import React, { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { testIdGen, useDimensions } from '../../utils/index.js';
import { Button } from '../Button/index.js';
import { Body, Title } from '../Typography/index.js';
import { getAlignmentStyle, getAniTransitionConfig, getBorderStyle, getPosition } from './sheet.logic.js';
import { useSheetId } from './useSheetId/index.js';
import { useUniqueId } from './useUniqueId/useUniqueId.js';
/** This component renders a Sheet to right/bottom/left.
 *
 */
export function Sheet({ addClassName, children, dataMetrics, dataTestId, title, subtitle, actions, isOpen = false, onClose, enableClose = true, position = 'right', isSticky = false, elementRef = null, leftRef = null, showMask = false, size, ...passThrough }) {
    const id = useUniqueId();
    const [sheetId, setSheetId] = useSheetId();
    useEffect(() => {
        return () => {
            if (sheetId === id)
                setSheetId('');
        };
    }, [sheetId, id, setSheetId]);
    useEffect(() => {
        if (sheetId !== '' && sheetId !== id) {
            onClose();
        }
    }, [sheetId, id, onClose]);
    useEffect(() => {
        if (isOpen && sheetId === '') {
            setSheetId(id);
        }
        if (!isOpen && sheetId === id) {
            setSheetId('');
        }
    }, [isOpen, id, setSheetId, sheetId]);
    const opened = useMemo(() => {
        return sheetId === id;
    }, [sheetId, id]);
    const intl = useIntl();
    const dimensions = useDimensions({ ref: elementRef || leftRef });
    const aniTransitionConfigs = getAniTransitionConfig(position, size);
    const sheetClasses = twMerge('twds-box-border twds-font-lato twds-bg-white twds-shadow dark:twds-shadow-dark-sheet twds-z-10 dark:twds-bg-gray-950 ', 'twds-flex twds-flex-col twds-overflow-hidden', 'twds-h-full', getAlignmentStyle(position, isSticky || position === 'permanent'), getBorderStyle({ align: position, isSticky: isSticky || position === 'permanent' }), isSticky || position === 'permanent' ? 'twds-absolute' : 'twds-fixed', addClassName);
    const transitions = useTransition(opened, aniTransitionConfigs);
    return (React.createElement(React.Fragment, null,
        showMask && (React.createElement(OverlayMask, { isOpen: isOpen, onClick: onClose, label: intl.formatMessage({
                defaultMessage: 'Close',
                id: 'tRQMci',
                description: 'Close Sheet'
            }) })),
        transitions((styles, item) => item && (React.createElement(animated.div, { "data-metrics": dataMetrics, "data-testid": dataTestId ?? (typeof title === 'string' && `sheet-${testIdGen(title)}`), style: {
                ...styles,
                ...getPosition(position, isSticky, dimensions)
            }, className: sheetClasses, ...passThrough },
            React.createElement("div", { className: "twds-flex twds-flex-col twds-h-full" },
                React.createElement("div", { className: classNames('twds-border-0 twds-border-solid twds-border-gray-200 twds-flex twds-justify-between twds-px-4 twds-py-2 twds-space-x-2 twds-border-b dark:twds-border-gray-950', 'twds-border-t') },
                    React.createElement("div", { 
                        // Both twds-min-w-0 here and below are required for text truncating to work
                        className: "twds-flex twds-space-x-1 twds-items-center twds-min-w-0" },
                        React.createElement("div", { className: "twds-min-w-0 twds-flex twds-flex-col", "aria-label": "sheet header" },
                            React.createElement(Title, { level: 2, size: "xxs", addClassName: "twds-flex dark:twds-text-gray-100" }, title),
                            subtitle && (React.createElement(Body, { size: "sm", addClassName: "twds-flex twds-text-secondary dark:twds-text-gray-300" }, subtitle)))),
                    React.createElement("div", { className: "twds-flex twds-space-x-2" },
                        actions,
                        enableClose && (React.createElement(Button, { onClick: onClose, appearance: "tertiary-clear", "aria-label": intl.formatMessage({
                                defaultMessage: 'Close',
                                id: 'tRQMci',
                                description: 'Close Sheet'
                            }), size: "sm", icon: React.createElement(CloseIcon, { size: "xs", className: "dark:twds-text-gray-400" }) })))),
                React.createElement("div", { className: "twds-flex twds-flex-col twds-overflow-auto twds-scrollbar-default dark:twds-scrollbar-track-dark dark:twds-scrollbar-thumb-dark dark:twds-scrollbar-corner-dark twds-flex-grow" }, children)))))));
}
/**
 * The OverlayMask component is used to create a mask, often behind a Modal or some other popup.
 */
export function OverlayMask({ isOpen, label, onClick }) {
    const transitions = useTransition(isOpen, {
        from: { opacity: 0, zIndex: 10 },
        enter: { opacity: 0.8 },
        leave: { opacity: 0 },
        config: { clamp: true }
    });
    const classes = classNames('twds-inset-0 twds-bg-steel-gray-400 dark:twds-bg-steel-gray-1000 twds-fixed', onClick && 'twds-cursor-default');
    return transitions((styles, item) => item && React.createElement(animated.div, { style: styles, className: classes, "aria-label": label, onClick: onClick }));
}
//# sourceMappingURL=Sheet.js.map